import React, {
  useCallback,
  useMemo,
  useEffect,
  useRef,
  useState,
} from "react";
import { Editor, Transforms, Path } from "slate";
import { ReactEditor } from "slate-react";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import {
  AIComparisonContent,
  AIContentElement,
  AIHierarchyContent,
  AITemplateContent,
  AITemplateName,
  AITimelineContent,
  AICycleContent,
  AIFlowchartContent,
  AIUnifiedContent,
} from "../types/ai-templates";
import { useTranslation } from "react-i18next";

interface TemplateInfo {
  type: "comparison" | "unified";
  childCount: number;
  levelCount?: number;
  aspectCount?: number;
  imageUrl: {
    tr: string;
    en: string;
    de: string; // Added German support
  };
  component: string;
  templateType: AITemplateName;
  templateName: string;
  originalTemplateName: string;
}

interface AutographicsProps {
  editor: Editor;
  aiResult: {
    template_contents: {
      [key: string]: AITemplateContent;
    };
  };
  targetPath: Path;
  autoCalculatePosition: boolean;
  onClose: () => void;
  onSelectTemplate: (template: string, content: AITemplateContent) => void;
}

const TEMPLATE_REGISTRY: Omit<
  TemplateInfo,
  "templateName" | "originalTemplateName"
>[] = [
  {
    type: "comparison",
    childCount: 2,
    imageUrl: {
      tr: "/src/packages/assets/templates/tr/comparison_2_2.png",
      en: "/src/packages/assets/templates/en/comparison_2_2.png",
      de: "/src/packages/assets/templates/de/comparison_2_2.png", // Added German path
    },
    component: "ComparisonTemplate_2",
    templateType: "comparison",
  },

  // Timeline Templates
  {
    type: "unified",
    levelCount: 1,
    childCount: 2,
    imageUrl: {
      tr: "/src/packages/assets/templates/tr/timeline_2.png",
      en: "/src/packages/assets/templates/en/timeline_2.png",
      de: "/src/packages/assets/templates/de/timeline_2.png", // Added German path
    },
    component: "TimelineTemplate_2",
    templateType: "timeline",
  },
  {
    type: "unified",
    levelCount: 1,
    childCount: 3,
    imageUrl: {
      tr: "/src/packages/assets/templates/tr/timeline_3.png",
      en: "/src/packages/assets/templates/en/timeline_3.png",
      de: "/src/packages/assets/templates/de/timeline_3.png", // Added German path
    },
    component: "TimelineTemplate_3",
    templateType: "timeline",
  },
  {
    type: "unified",
    levelCount: 1,
    childCount: 4,
    imageUrl: {
      tr: "/src/packages/assets/templates/tr/timeline_4.png",
      en: "/src/packages/assets/templates/en/timeline_4.png",
      de: "/src/packages/assets/templates/de/timeline_4.png", // Added German path
    },
    component: "TimelineTemplate_4",
    templateType: "timeline",
  },
  {
    type: "unified",
    levelCount: 1,
    childCount: 5,
    imageUrl: {
      tr: "/src/packages/assets/templates/tr/timeline_5.png",
      en: "/src/packages/assets/templates/en/timeline_5.png",
      de: "/src/packages/assets/templates/de/timeline_5.png", // Added German path
    },
    component: "TimelineTemplate_5",
    templateType: "timeline",
  },
  // Cycle Templates
  {
    type: "unified",
    levelCount: 1,
    childCount: 3,
    imageUrl: {
      tr: "/src/packages/assets/templates/tr/cycle_3.png",
      en: "/src/packages/assets/templates/en/cycle_3.png",
      de: "/src/packages/assets/templates/de/cycle_3.png", // Added German path
    },
    component: "CycleTemplate_3",
    templateType: "cycle",
  },
  {
    type: "unified",
    levelCount: 1,
    childCount: 4,
    imageUrl: {
      tr: "/src/packages/assets/templates/tr/cycle_4.png",
      en: "/src/packages/assets/templates/en/cycle_4.png",
      de: "/src/packages/assets/templates/de/cycle_4.png", // Added German path
    },
    component: "CycleTemplate_4",
    templateType: "cycle",
  },
  {
    type: "unified",
    levelCount: 1,
    childCount: 5,
    imageUrl: {
      tr: "/src/packages/assets/templates/tr/cycle_5.png",
      en: "/src/packages/assets/templates/en/cycle_5.png",
      de: "/src/packages/assets/templates/de/cycle_5.png", // Added German path
    },
    component: "CycleTemplate_5",
    templateType: "cycle",
  },
  // Hierarchy Template
  {
    type: "unified",
    levelCount: 2,
    childCount: 3,
    imageUrl: {
      tr: "/src/packages/assets/templates/tr/hierarchy.png",
      en: "/src/packages/assets/templates/en/hierarchy.png",
      de: "/src/packages/assets/templates/de/hierarchy.png", // Added German path
    },
    component: "HierarchyTemplate",
    templateType: "hierarchy",
  },
  // Flowchart Templates
  {
    type: "unified",
    levelCount: 1,
    childCount: 2,
    imageUrl: {
      tr: "/src/packages/assets/templates/tr/flowchart_2.png",
      en: "/src/packages/assets/templates/en/flowchart_2.png",
      de: "/src/packages/assets/templates/de/flowchart_2.png", // Added German path
    },
    component: "FlowchartTemplate_2",
    templateType: "flowchart",
  },
  {
    type: "unified",
    levelCount: 1,
    childCount: 3,
    imageUrl: {
      tr: "/src/packages/assets/templates/tr/flowchart_3.png",
      en: "/src/packages/assets/templates/en/flowchart_3.png",
      de: "/src/packages/assets/templates/de/flowchart_3.png", // Added German path
    },
    component: "FlowchartTemplate_3",
    templateType: "flowchart",
  },
  {
    type: "unified",
    levelCount: 1,
    childCount: 4,
    imageUrl: {
      tr: "/src/packages/assets/templates/tr/flowchart_4.png",
      en: "/src/packages/assets/templates/en/flowchart_4.png",
      de: "/src/packages/assets/templates/de/flowchart_4.png", // Added German path
    },
    component: "FlowchartTemplate_4",
    templateType: "flowchart",
  },
  {
    type: "unified",
    levelCount: 1,
    childCount: 5,
    imageUrl: {
      tr: "/src/packages/assets/templates/tr/flowchart_5.png",
      en: "/src/packages/assets/templates/en/flowchart_5.png",
      de: "/src/packages/assets/templates/de/flowchart_5.png", // Added German path
    },
    component: "FlowchartTemplate_5",
    templateType: "flowchart",
  },
  {
    type: "unified",
    levelCount: 1,
    childCount: 6,
    imageUrl: {
      tr: "/src/packages/assets/templates/tr/flowchart_6.png",
      en: "/src/packages/assets/templates/en/flowchart_6.png",
      de: "/src/packages/assets/templates/de/flowchart_6.png", // Added German path
    },
    component: "FlowchartTemplate_6",
    templateType: "flowchart",
  },
];

// ✅ Enhanced Helper Functions
const getContentChildCount = (content: AITemplateContent): number => {
  if (!content) return 1;

  if ("sides" in content && Array.isArray(content.sides)) {
    return content.sides.length;
  }
  if ("steps" in content && Array.isArray(content.steps)) {
    return content.steps.length;
  }
  if ("levels" in content && Array.isArray(content.levels)) {
    if (content.levels.length > 0 && content.levels[0]?.items) {
      return content.levels[0].items.length;
    }
    return content.levels.length;
  }
  return 1;
};

// ✅ Improved template type detection
const getContentTemplateType = (templateName: string): AITemplateName => {
  const lowerName = templateName.toLowerCase();

  if (lowerName.includes("comparison")) return "comparison";
  if (lowerName.includes("unified")) return "unified";
  return "comparison";
};

// Template Card Component
const TemplateCard = React.memo<{
  template: TemplateInfo;
  isSelected: boolean;
  onSelect: (template: TemplateInfo) => void;
}>(({ template, isSelected, onSelect }) => {
  const handleClick = useCallback(() => {
    console.log("🎯 Template card clicked:", template.templateName);
    onSelect(template);
  }, [template, onSelect]);
  const { i18n, t } = useTranslation();

  // Enhanced language support with fallback to English
  const getTemplateImageUrl = useCallback(
    (template: TemplateInfo) => {
      const currentLanguage = i18n.language as "tr" | "en" | "de";

      // Check if the current language is supported, otherwise fallback to English
      if (template.imageUrl[currentLanguage]) {
        return template.imageUrl[currentLanguage];
      }

      // Fallback to English if current language is not available
      return template.imageUrl.en;
    },
    [i18n.language],
  );

  return (
    <div
      className={`
    p-1 pr-3 h-[116px] relative flex-shrink-0 snap-start cursor-pointer group
    ${isSelected ? "ring-2 ring-[#1ac6ff]" : ""}
  `}
      onClick={handleClick}
    >
      <div
        className={`
      relative rounded-md w-[168px] h-[110px] transition-all duration-300 ease-out
      border border-[#1ac6ff] overflow-hidden
      ${
        isSelected
          ? "border-opacity-100 shadow-[0_0_0_5px_rgba(26,198,255,0.157)]"
          : "border-opacity-0 group-hover:border-opacity-100 group-hover:shadow-[0_0_0_5px_rgba(26,198,255,0.098)]"
      }
    `}
        style={{
          backgroundImage: `url(${getTemplateImageUrl(template)})`,
          backgroundSize: "contain",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute bottom-0 w-full bg-white/80 text-center py-1">
          <span className="text-sm font-medium text-gray-700">
            {t(`articleGenerator.templates.${template.templateType}`)}
          </span>
        </div>

        {isSelected && (
          <div className="absolute top-2 right-2 w-5 h-5 bg-[#1ac6ff] rounded-full flex items-center justify-center">
            <CheckIcon className="w-3 h-3 text-white" />
          </div>
        )}
      </div>
    </div>
  );
});

TemplateCard.displayName = "TemplateCard";

// Main Autographics Component
export const Autographics = React.memo<AutographicsProps>(
  ({
    editor,
    aiResult,
    targetPath,
    onClose,
    autoCalculatePosition = true,
    onSelectTemplate,
  }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { t, i18n } = useTranslation();
    const [isVisible, setIsVisible] = useState(false);
    const [selectedTemplateInfo, setSelectedTemplateInfo] =
      useState<TemplateInfo | null>(null);
    const [previewNodePath, setPreviewNodePath] = useState<Path | null>(null);
    const [currentAINodePath, setCurrentAINodePath] = useState<Path | null>(
      null,
    );
    const [modalPosition, setModalPosition] = useState<{
      top: number;
      left: number;
    } | null>(null);

    console.log("🎨 Autographics rendered with:", { aiResult, targetPath });

    // Calculate modal position based on targetPath
    const calculateModalPosition = useCallback(() => {
      try {
        if (!autoCalculatePosition) return;
        const domNode = ReactEditor.toDOMNode(editor, editor);
        const targetNode = Editor.node(editor, targetPath);

        if (targetNode) {
          const targetDOMNode = ReactEditor.toDOMNode(editor, targetNode[0]);
          const targetRect = targetDOMNode.getBoundingClientRect();
          const editorRect = domNode.getBoundingClientRect();

          // Position modal to the left of the target node
          const position = {
            top: targetRect.top - editorRect.top,
            left: targetRect.left - editorRect.left - 10, // 10px offset
          };

          setModalPosition(position);
        }
      } catch (error) {
        console.error("❌ Error calculating modal position:", error);

        // Try with targetPath-1 before falling back
        try {
          const previousPath = Path.previous(targetPath);
          const previousNode = Editor.node(editor, previousPath);

          if (previousNode) {
            const domNode = ReactEditor.toDOMNode(editor, editor);
            const previousDOMNode = ReactEditor.toDOMNode(
              editor,
              previousNode[0],
            );
            const previousRect = previousDOMNode.getBoundingClientRect();
            const editorRect = domNode.getBoundingClientRect();

            const position = {
              top: previousRect.top - editorRect.top,
              left: previousRect.left - editorRect.left - 10, // 10px offset
            };

            setModalPosition(position);
            console.log(
              "✅ Modal positioned using previous path:",
              previousPath,
            );
            return;
          }
        } catch (previousError) {
          console.error("❌ Error with previous path:", previousError);
        }

        // Final fallback position
        setModalPosition({ top: 0, left: 200 });
      }
    }, [editor, targetPath]);

    useEffect(() => {
      const timer = setTimeout(() => {
        calculateModalPosition();
        setIsVisible(true);
      }, 50);
      return () => clearTimeout(timer);
    }, [calculateModalPosition]);

    // Recalculate position on window resize or scroll
    useEffect(() => {
      const handleResize = () => {
        if (isVisible) {
          calculateModalPosition();
        }
      };

      const handleScroll = () => {
        if (isVisible) {
          calculateModalPosition();
        }
      };

      window.addEventListener("resize", handleResize);
      window.addEventListener("scroll", handleScroll, true);

      return () => {
        window.removeEventListener("resize", handleResize);
        window.removeEventListener("scroll", handleScroll, true);
      };
    }, [isVisible, calculateModalPosition]);

    // ✅ IMPROVED: Process templates with better data transformation
    const { templates, defaultTemplate } = useMemo(() => {
      console.log("📋 Processing AI result:", aiResult);

      if (
        !aiResult?.template_contents ||
        Object.keys(aiResult.template_contents).length === 0
      ) {
        console.warn("❌ No valid template_contents in aiResult:", aiResult);
        return { templates: [], defaultTemplate: null };
      }

      const templatesList: TemplateInfo[] = [];
      const templateEntries = Object.entries(aiResult.template_contents);
      let firstTemplate: TemplateInfo | null = null;

      console.log("🔍 Processing template entries:", templateEntries);

      for (const [originalTemplateName, content] of templateEntries) {
        console.log(`🎯 Processing template: ${originalTemplateName}`, content);

        if (!content || typeof content !== "object") {
          console.warn(
            `❌ Invalid content for template ${originalTemplateName}:`,
            content,
          );
          continue;
        }

        const contentChildCount = getContentChildCount(content);
        const contentTemplateType =
          getContentTemplateType(originalTemplateName);

        console.log(
          `📊 Template ${originalTemplateName}: childCount=${contentChildCount}, type=${contentTemplateType}`,
        );

        // Find matching templates
        const possibleTemplates = TEMPLATE_REGISTRY.filter((template) => {
          if (contentTemplateType === "comparison") {
            return (
              template.templateType === "comparison" &&
              template.childCount >= Math.min(contentChildCount, 2) &&
              template.childCount <= Math.min(contentChildCount, 4)
            );
          }

          if (contentTemplateType === "unified") {
            const levelCount =
              (content as AIUnifiedContent).levels?.length || 1;
            // For unified content, exclude comparison templates
            if (template.templateType === "comparison") {
              return false;
            }

            // For hierarchy templates, check level count
            if (template.templateType === "hierarchy") {
              return template.templateType === "hierarchy" && levelCount >= 1;
            }

            // For level 1 templates (timeline, cycle, flowchart), allow any non-comparison template
            if (template.levelCount === 1 && levelCount === 1) {
              return template.childCount === contentChildCount;
            }

            // For other cases, match by template type and child count
            return (
              template.templateType === contentTemplateType &&
              template.childCount === contentChildCount
            );
          }

          return false;
        });

        console.log(
          `🎨 Found ${possibleTemplates.length} possible templates for ${originalTemplateName}`,
        );

        // Sort by best match
        const sortedPossibleTemplates = possibleTemplates.sort((a, b) => {
          const aExactMatch = a.childCount === contentChildCount ? 1 : 0;
          const bExactMatch = b.childCount === contentChildCount ? 1 : 0;
          if (aExactMatch !== bExactMatch) return bExactMatch - aExactMatch;

          const aSameType = a.templateType === contentTemplateType ? 1 : 0;
          const bSameType = b.templateType === contentTemplateType ? 1 : 0;
          if (aSameType !== bSameType) return bSameType - aSameType;

          return (
            Math.abs(a.childCount - contentChildCount) -
            Math.abs(b.childCount - contentChildCount)
          );
        });

        // Add all possible templates
        for (const template of sortedPossibleTemplates) {
          const templateInfo: TemplateInfo = {
            ...template,
            templateName: `${originalTemplateName}_${template.component}`,
            originalTemplateName,
          };

          templatesList.push(templateInfo);

          if (!firstTemplate) {
            firstTemplate = templateInfo;
            console.log("🎯 Default template set:", templateInfo);
          }
        }
      }

      return {
        templates: templatesList,
        defaultTemplate: firstTemplate,
      };
    }, [aiResult.template_contents]);

    // ✅ CRITICAL: Enhanced content transformation
    const transformContentForTemplate = useCallback(
      (
        content: AITemplateContent,
        template: TemplateInfo,
      ): AITemplateContent => {
        console.log(
          "🔄 Transforming content for template:",
          template.templateType,
          template.childCount,
        );
        console.log("🔍 Input content structure:", Object.keys(content));

        // For comparison templates
        if (template.templateType === "comparison") {
          const comparisonContent = content as AIComparisonContent;
          const transformedContent = {
            ...comparisonContent,
            sides: comparisonContent.sides?.slice(0, template.childCount) || [],
          };
          console.log("✅ Comparison content transformed:", transformedContent);
          return transformedContent;
        }

        // For hierarchy templates
        if (template.templateType === "hierarchy") {
          const hierarchyContent = content as AIHierarchyContent;
          const transformedContent = {
            ...hierarchyContent,
            levels:
              hierarchyContent.levels?.map((level) => ({
                ...level,
                items:
                  level.items?.slice(
                    0,
                    Math.ceil(
                      template.childCount /
                        (hierarchyContent.levels?.length || 1),
                    ),
                  ) || [],
              })) || [],
          };
          console.log("✅ Hierarchy content transformed:", transformedContent);
          return transformedContent;
        }

        // ✅ CRITICAL: For unified templates (timeline, cycle, flowchart)
        if (template.type === "unified") {
          // Case 1: Content has levels - convert to steps
          if ("levels" in content) {
            const hierarchyContent = content as AIHierarchyContent;
            console.log(
              "🔄 Converting levels to steps:",
              hierarchyContent.levels,
            );

            if (hierarchyContent.levels && hierarchyContent.levels.length > 0) {
              // Flatten all items from all levels
              const allItems = hierarchyContent.levels.flatMap(
                (level) => level.items || [],
              );
              console.log("📝 Flattened items:", allItems);

              const transformedContent = {
                title: hierarchyContent.title || "Untitled",
                genericColors: hierarchyContent.genericColors || [
                  "#3B82F6",
                  "#EF4444",
                  "#10B981",
                  "#F59E0B",
                  "#8B5CF6",
                ],
                steps: allItems
                  .slice(0, template.childCount)
                  .map((item, index) => ({
                    title: item.title || `Step ${index + 1}`,
                    description: item.description || "No description provided",
                    icon_description: item.icon_description,
                    iconId: item.iconId,
                    color: item.color,
                  })),
              } as AITimelineContent | AICycleContent | AIFlowchartContent;

              console.log(
                "✅ Levels to steps transformation:",
                transformedContent,
              );
              return transformedContent;
            }
          }

          // Case 2: Content already has steps - just slice
          if ("steps" in content) {
            const stepsContent = content as
              | AITimelineContent
              | AICycleContent
              | AIFlowchartContent;
            const transformedContent = {
              ...stepsContent,
              title: stepsContent.title || "Untitled",
              genericColors: stepsContent.genericColors || [
                "#3B82F6",
                "#EF4444",
                "#10B981",
                "#F59E0B",
                "#8B5CF6",
              ],
              steps: (stepsContent.steps || [])
                .slice(0, template.childCount)
                .map((step, index) => ({
                  title: step.title || `Step ${index + 1}`,
                  description: step.description || "No description provided",
                  icon_description: step.icon_description,
                  iconId: step.iconId,
                  color: step.color,
                })),
            };
            console.log("✅ Steps content transformed:", transformedContent);
            return transformedContent;
          }

          // Case 3: Fallback - create minimal structure
          console.warn("⚠️ No compatible structure found, creating fallback");
          const fallbackContent = {
            title: "Generated Content",
            genericColors: [
              "#3B82F6",
              "#EF4444",
              "#10B981",
              "#F59E0B",
              "#8B5CF6",
            ],
            steps: Array.from(
              { length: Math.min(template.childCount, 3) },
              (_, index) => ({
                title: `Step ${index + 1}`,
                description: "Generated step description",
                icon_description: "Default icon",
                iconId: "default",
                color: ["#3B82F6", "#EF4444", "#10B981"][index] || "#3B82F6",
              }),
            ),
          } as AITimelineContent | AICycleContent | AIFlowchartContent;

          console.log("🔧 Fallback content created:", fallbackContent);
          return fallbackContent;
        }

        console.log("✅ Content returned unchanged");
        return content;
      },
      [],
    );

    // Create preview node
    const createPreviewNode = useCallback(
      (template: TemplateInfo) => {
        console.log(
          "🎨 Creating preview node for template:",
          template.templateName,
        );

        const content =
          aiResult.template_contents[template.originalTemplateName];
        if (!content) {
          console.warn(
            "❌ No content found for template:",
            template.originalTemplateName,
          );
          return;
        }

        const transformedContent = transformContentForTemplate(
          content,
          template,
        );
        console.log("🎯 Preview node content:", transformedContent);

        const previewNode: AIContentElement = {
          type: "ai-content",
          selected_template: template.component,
          aiResult: aiResult,
          template_content: transformedContent,
          children: [{ text: "" }],
        };

        try {
          if (!autoCalculatePosition) {
            // 🟢 Replace the node at targetPath
            Transforms.removeNodes(editor, { at: targetPath });
            Transforms.insertNodes(editor, previewNode, {
              at: targetPath,
              select: false,
            });
            setPreviewNodePath(targetPath);
            console.log("♻️ Replaced node at path:", targetPath);
          } else {
            // 🔵 Default behavior: insert after targetPath
            const nextPath = Path.next(targetPath);
            const isNextPathValid = Editor.hasPath(editor, nextPath);

            const insertPath = isNextPathValid
              ? nextPath
              : [editor.children.length]; // fallback: insert at end

            Transforms.insertNodes(editor, previewNode, {
              at: insertPath,
              select: false,
            });
            setPreviewNodePath(insertPath);
            console.log("✅ Preview node created at path:", insertPath);
          }
        } catch (error) {
          console.error("❌ Error creating preview node:", error);
        }
      },
      [
        editor,
        aiResult,
        targetPath,
        autoCalculatePosition,
        transformContentForTemplate,
      ],
    );

    // Update preview node
    const updatePreviewNode = useCallback(
      (template: TemplateInfo) => {
        if (!previewNodePath) {
          console.log("⚠️ No preview node path, creating new preview");
          createPreviewNode(template);
          return;
        }

        console.log(
          "🔄 Updating preview node with template:",
          template.templateName,
        );

        const content =
          aiResult.template_contents[template.originalTemplateName];
        if (!content) {
          console.warn(
            "❌ No content found for template:",
            template.originalTemplateName,
          );
          return;
        }

        const transformedContent = transformContentForTemplate(
          content,
          template,
        );
        console.log("🎯 Updated content:", transformedContent);

        try {
          Transforms.setNodes(
            editor,
            {
              selected_template: template.component,
              template_content: transformedContent,
            } as Partial<AIContentElement>,
            { at: previewNodePath },
          );

          console.log(
            "✅ Preview node updated with template:",
            template.component,
          );
        } catch (error) {
          console.error("❌ Error updating preview node:", error);
          createPreviewNode(template);
        }
      },
      [
        aiResult.template_contents,
        editor,
        previewNodePath,
        transformContentForTemplate,
        createPreviewNode,
      ],
    );

    // Initialize with default template
    useEffect(() => {
      if (
        defaultTemplate &&
        !selectedTemplateInfo &&
        !previewNodePath &&
        autoCalculatePosition // ✅ prevent init replacement on first render
      ) {
        console.log(
          "🚀 Setting up initial template:",
          defaultTemplate.templateName,
        );
        setSelectedTemplateInfo(defaultTemplate);
        createPreviewNode(defaultTemplate);
      }
    }, [
      defaultTemplate,
      selectedTemplateInfo,
      previewNodePath,
      autoCalculatePosition,
      createPreviewNode,
    ]);

    // Handle template selection
    const handleTemplateSelect = useCallback(
      (template: TemplateInfo) => {
        console.log("🎯 Template selected:", template.templateName);
        setSelectedTemplateInfo(template);
        updatePreviewNode(template);

        const content =
          aiResult.template_contents[template.originalTemplateName];
        if (content) {
          const transformedContent = transformContentForTemplate(
            content,
            template,
          );
          onSelectTemplate(template.component, transformedContent);
        }
      },
      [
        aiResult.template_contents,
        updatePreviewNode,
        onSelectTemplate,
        transformContentForTemplate,
      ],
    );

    // Handle finish
    const handleFinish = useCallback(() => {
      console.log("🎉 Finishing template selection");
      if (previewNodePath && selectedTemplateInfo) {
        setCurrentAINodePath(previewNodePath);
        console.log(
          "✅ Template selection finished, node saved at:",
          previewNodePath,
        );
      }
      onClose();
    }, [previewNodePath, selectedTemplateInfo, onClose]);

    // Handle close
    const handleClose = useCallback(() => {
      console.log("🚪 Closing Autographics");
      if (previewNodePath && !currentAINodePath) {
        try {
          Transforms.removeNodes(editor, { at: previewNodePath });
          console.log("🗑️ Preview node removed on close");
        } catch (error) {
          console.error("❌ Error removing preview node:", error);
        }
      }
      onClose();
    }, [previewNodePath, currentAINodePath, editor, onClose]);

    // Enhanced no templates message with German support
    const getNoTemplatesMessage = useCallback(() => {
      switch (i18n.language) {
        case "tr":
          return "Şu anda uygun template bulunamadı";
        case "de":
          return "Derzeit sind keine passenden Vorlagen verfügbar";
        default:
          return "No suitable templates available at the moment";
      }
    }, [i18n.language]);

    const getCloseButtonText = useCallback(() => {
      switch (i18n.language) {
        case "tr":
          return "Kapat";
        case "de":
          return "Schließen";
        default:
          return "Close";
      }
    }, [i18n.language]);

    if (!isVisible || (!modalPosition && autoCalculatePosition)) return null;

    if (templates.length === 0) {
      return (
        <div
          ref={containerRef}
          className="absolute pointer-events-auto z-50 cursor-default shadow-lg rounded-xl max-w-[194px]"
          style={{
            top: `${modalPosition?.top}px`,
            left: `${modalPosition?.left}px`,
          }}
        >
          <div className="h-[200px] w-[194px] rounded-xl backdrop-blur-sm bg-white/80 dark:bg-[rgba(65,87,104,0.8)] border border-[#1ac6ff] text-[#1ac6ff] flex flex-col items-center justify-center p-4">
            <div className="text-sm text-center mb-4">
              {getNoTemplatesMessage()}
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 text-xs bg-[#1ac6ff] text-white rounded-md hover:bg-[#1ac6ff]/80 transition-colors"
            >
              {getCloseButtonText()}
            </button>
          </div>
        </div>
      );
    }
    const modalClass = autoCalculatePosition
      ? "absolute pointer-events-auto cursor-default transition-all duration-300 ease-out shadow-[0_0_8px_0_rgba(0,0,0,0.05),0_4px_4px_0_rgba(0,0,0,0.1)] rounded-xl max-w-[194px]"
      : "pointer-events-auto cursor-default transition-all duration-300 ease-out shadow-[0_0_8px_0_rgba(0,0,0,0.05),0_4px_4px_0_rgba(0,0,0,0.1)] rounded-xl max-w-[194px]";
    const scrollableClass = !autoCalculatePosition
      ? "max-h-[280px]"
      : "max-h-[480px]";
    return (
      <div
        ref={containerRef}
        className={modalClass}
        style={
          autoCalculatePosition
            ? {
                top: `${modalPosition?.top}px`,
                left: `${modalPosition?.left}px`,
              }
            : {}
        }
      >
        <div className="h-full w-[194px] relative rounded-xl backdrop-blur-sm bg-white/80 dark:bg-[rgba(65,87,104,0.8)] border border-[#1ac6ff] text-[#1ac6ff]">
          {/* Header */}
          <div className="h-8 bg-[#1ac6ff]/10 border-b border-[#1ac6ff]/20 flex items-center justify-center">
            <span className="text-xs font-medium text-[#1ac6ff]">
              {t("articleGenerator.selectTemplate")} ({templates.length})
            </span>
          </div>

          {/* Templates List */}
          <div
            className={`flex flex-col items-center overflow-x-hidden overflow-y-auto pt-[9px] scrollbar-none ${scrollableClass}`}
          >
            {templates.map((template, index) => (
              <TemplateCard
                key={`${template.templateName}-${index}`}
                template={template}
                isSelected={
                  selectedTemplateInfo?.templateName === template.templateName
                }
                onSelect={handleTemplateSelect}
              />
            ))}
          </div>

          {/* Footer */}
          <div className="h-12 bg-[#1ac6ff]/10 border-t border-[#1ac6ff]/20 flex items-center justify-center gap-2">
            <button
              onClick={handleClose}
              className="p-1.5 text-xs text-[#1ac6ff] hover:text-[#1ac6ff]/80 transition-colors duration-200"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
            <button
              onClick={handleFinish}
              className="px-4 py-1.5 text-xs font-medium bg-[#1ac6ff] text-white rounded-md hover:bg-[#1ac6ff]/80 transition-colors duration-200 flex items-center gap-1 disabled:opacity-50"
              disabled={!selectedTemplateInfo}
            >
              <CheckIcon className="w-3 h-3" />
              {t("common.ok")}
            </button>
          </div>
        </div>
      </div>
    );
  },
);

Autographics.displayName = "Autographics";
